import React, {MouseEventHandler, useEffect, useRef, useState} from 'react'
import styled, {useTheme} from 'styled-components'
import {Transition} from 'react-transition-group'
import Button from '../button/Button'
import {CheckMark, Planet} from '../icons'
import {VisuallyHidden} from '../text'
import Box from '../layout/Box'
import Link from '../link/Link'
import useOnClickOutside from '../../hooks/useOnClickOutside'

type TranslationLink = {
  locale: string
  localeLabel?: string
  url: string
}

type PopupProps = {
  isExpanded: boolean
  currentLocale: LanguageSwitcherProps['currentLocale']
  translations: LanguageSwitcherProps['translations']
  onCloseClick: MouseEventHandler<HTMLButtonElement>
  onSelect: LanguageSwitcherProps['onSelect']
  position?: LanguageSwitcherProps['position']
  setExpanded: (isExpanded: boolean) => void
}

interface LanguageSwitcherProps {
  color?: string
  position?: string
  currentLocale: Omit<TranslationLink, 'url'>
  translations: TranslationLink[]
  onSelect: (e: React.MouseEvent<HTMLAnchorElement>, link: TranslationLink) => void
}

const SwitcherContainer = styled(Box).attrs({
  $styles: {
    position: 'relative',
    zIndex: 100,
  },
})``

const PopupListWrapper = styled(Box).attrs<{$isExpanded: boolean; $position?: string}>(
  ({$styles = {}, theme, $isExpanded, $position}) => ({
    $p: [theme.spaces.md, theme.spaces.lg],
    $styles: {
      ...$styles,
      position: ['fixed', 'fixed', 'absolute'],
      top: $position === 'top' ? 'auto' : [null, null, 30],
      bottom: $position === 'top' ? [null, null, 30] : 'auto',
      left: [0, 0, -24],
      right: [0, 0, null],
      minWidth: 248,
      borderRadius: [null, null, 4],
      boxShadow: $isExpanded ? '0 4px 16px 0 rgba(5, 0, 56, 0.16)' : null,
      background: theme.colors.primaryWhite,
      textAlign: 'left',
      ':before': {
        content: `''`,
        height: 6,
        position: 'absolute',
        left: 0,
        right: 0,
        top: $position === 'top' ? null : -6,
        bottom: $position === 'top' ? -6 : null,
      },
    },
  }),
)<{$isExpanded: boolean; $position?: string}>`
  ${({theme, $isExpanded}) =>
    theme.normalizeStyles({
      bottom: $isExpanded ? [0, 0, null] : ['-100%', '-100%', null],
    })}
`

const PopupList = styled(Box).attrs({
  as: 'ul',
  role: 'menu',
})`
  li:first-child {
    ${props =>
      props.theme.normalizeStyles({
        paddingTop: [null, 0],
      })};
  }
  li:last-child {
    ${props =>
      props.theme.normalizeStyles({
        paddingBottom: [null, 0],
      })};
  }
`

const PopupListItem = styled(Box).attrs({
  as: 'li',
  $styles: {
    position: 'relative',
  },
  $py: 8,
})``

const PopupTitle = styled(Box).attrs(props => ({
  $styles: {
    display: ['block', 'block', 'none'],
    fontSize: 16,
    lineHeight: 24,
    fontWeight: 500,
    color: props.theme.colors.primaryIndigo,
    position: 'relative',
  },
  $pt: 8,
  $pb: 16,
}))``

const PopupCloseButton = styled(Button).attrs({
  $styles: {
    width: 32,
    height: 32,
    position: 'absolute',
    top: '50%',
    right: -4,
    transform: 'translateY(-50%) rotate(45deg)',
  },
})``

const PopupCloseLine = styled(Box).attrs(props => ({
  $styles: {
    background: props.theme.colors.primaryIndigo,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}))`
  &:first-child {
    ${props =>
      props.theme.normalizeStyles({
        width: 22,
        height: '2px',
      })}
  }

  &:last-child {
    ${props =>
      props.theme.normalizeStyles({
        width: '2px',
        height: 22,
      })}
  }
`

const duration = 250

const collapsedStyles = {
  opacity: 0,
  visibility: 'hidden',
  transition: `box-shadow ${duration}ms 0ms, 
      transform ${duration}ms 0ms, 
      opacity ${duration}ms 0ms, 
      bottom ${duration}ms 0ms, 
      visibility 0ms ${duration}ms`,
  transform: 'translateY(0)',
}

const expandedStyles = {
  visibility: 'visible',
  transform: 'translateY(0)',
  transition: `visibility 0ms 0ms,
  opacity ${duration / 3}ms 0ms,
  bottom ${duration}ms 0ms,
  transform ${duration}ms 0ms,
  box-shadow ${duration / 3}ms
    ${duration / 4}ms;
  visibility: visible;
  opacity: 1;
  transform: translateY(0)
  `,
}

const transitionStyles = {
  entering: {opacity: 0, ...expandedStyles},
  entered: {opacity: 1, ...expandedStyles},
}

const TYPOGRAPHY_STYLES = {fontSize: [18, 18, 16, 18, 18], lineHeight: [32, 22, 22, 24, 24]}

const LanguageLink = styled(Link).attrs<{$isActive?: boolean}>(({$isActive, theme}) => ({
  $styles: {
    color: $isActive ? theme.colors.primaryBlue : theme.colors.primaryIndigo,
    transition: `color ${theme.animation.transitionDuration}`,
    textDecoration: 'none',
    ':hover': {
      color: theme.colors.primaryBlueHover,
    },
    ':focus': {
      ...theme.focus.outlines.withOffset,
    },
    width: '100%',
    ...TYPOGRAPHY_STYLES,
  },
}))<{$isActive?: boolean}>``

const LanguageCheckMark = styled(CheckMark)`
  ${props =>
    props.theme.normalizeStyles({
      position: 'absolute',
      top: '50%',
      right: 0,
      transform: 'translateY(-50%)',
    })}
`

const LanguagePopup = (props: PopupProps): React.ReactElement => {
  const {isExpanded, translations, onCloseClick, onSelect, position, currentLocale, setExpanded} =
    props
  const theme = useTheme()

  return (
    <Transition in={isExpanded} timeout={250}>
      {state => (
        <PopupListWrapper
          $isExpanded={isExpanded}
          $styles={{...(!isExpanded ? collapsedStyles : {}), ...transitionStyles[state]}}
          $position={position}
        >
          <PopupTitle>
            <Box $styles={{...TYPOGRAPHY_STYLES}}>Choose website language</Box>
            <PopupCloseButton onClick={onCloseClick}>
              <PopupCloseLine />
              <PopupCloseLine />
            </PopupCloseButton>
          </PopupTitle>

          <PopupList>
            {translations.map(t => (
              <PopupListItem key={t.locale}>
                <LanguageLink
                  data-locale={t.locale}
                  data-testid={`item-${t.locale}`}
                  onClick={e => {
                    setExpanded(false)
                    onSelect(e, t)
                  }}
                  href={t.url}
                  $isActive={t.locale === currentLocale.locale}
                >
                  {t.localeLabel || t.locale}
                </LanguageLink>

                {t.locale === currentLocale.locale && (
                  <LanguageCheckMark color={theme.colors.primaryBlue} />
                )}
              </PopupListItem>
            ))}
          </PopupList>
        </PopupListWrapper>
      )}
    </Transition>
  )
}

const SwitcherIcon = styled(Planet)`
  ${props =>
    props.theme.normalizeStyles({
      marginRight: [8, 8, 0, 8, 8],
      width: 24,
      height: 24,
    })}
`

const SwitcherCurrentLocale = styled(Box).attrs({
  $styles: {
    textTransform: 'uppercase',
  },
})``

const SwitcherButton = styled(Button).attrs<{$color?: string; $isActive: boolean}>(
  ({$color, theme, $isActive}) => {
    const activeColor = $color === 'white' ? theme.colors.outspoken : theme.colors.primaryBlueHover

    return {
      $styles: {
        fontSize: [18],
        lineHeight: 24,
        display: 'flex',
        alignItems: 'center',
        color: $isActive
          ? activeColor
          : $color === 'white'
          ? theme.colors.primaryWhite
          : theme.colors.primaryIndigo,
        ':hover': {
          color: activeColor,
        },
        ':focus': {
          ...theme.focus.outlines.withOffset,
        },
        transition: 'color 250ms ease',
      },
    }
  },
)<{$color?: string; $isActive: boolean}>``

const LanguageSwitcher = (props: LanguageSwitcherProps): React.ReactElement | null => {
  const {color, currentLocale, translations, onSelect} = props
  const [translationsState, setTranslationsState] = useState(translations)
  const [isExpanded, setExpanded] = useState(false)
  const ref = useRef<HTMLElement>(null)

  if (!currentLocale || translations.length === 0) {
    return null
  }

  useOnClickOutside(ref, () => {
    if (isExpanded) {
      setExpanded(false)
    }
  })

  useEffect(() => {
    if (!translations?.length) {
      return
    }

    // * put english first and sort the rest
    setTranslationsState(
      translations.sort((a, b) => {
        const EN = 'en'
        const labelA = a.localeLabel || ''
        const labelB = b.localeLabel || ''
        if (a.locale?.toLowerCase() == EN) {
          return -1
        }
        if (b.locale?.toLowerCase() == EN) {
          return +1
        }
        if (labelA > labelB) {
          return 1
        }
        if (labelB > labelA) {
          return -1
        }
        return 0
      }),
    )
  }, [translations])

  return (
    <SwitcherContainer data-track-place="Language Switcher" ref={ref}>
      <SwitcherButton
        data-testid="SwitcherButton"
        aria-haspopup={true}
        aria-expanded={isExpanded}
        $isActive={isExpanded}
        onClick={() => setExpanded(!isExpanded)}
        $color={color}
      >
        <SwitcherIcon aria-hidden="true" />
        <SwitcherCurrentLocale aria-hidden={true}>{currentLocale.locale}</SwitcherCurrentLocale>
        <VisuallyHidden>
          Current language is: {currentLocale.localeLabel || currentLocale.locale}
        </VisuallyHidden>
      </SwitcherButton>
      <LanguagePopup
        setExpanded={setExpanded}
        onSelect={onSelect}
        isExpanded={isExpanded}
        currentLocale={currentLocale}
        translations={translationsState}
        onCloseClick={() => setExpanded(false)}
        position={props.position}
      />
    </SwitcherContainer>
  )
}

export default Object.assign(LanguageSwitcher, {
  SwitcherCurrentLocale: SwitcherCurrentLocale,
})
